import {
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  SaveOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { CustomTableColumn } from '@hefe/models';
import { ModalsSlice, OpenModalActionPayload, TablesSlice } from '@hefe/store';
import { dateFormat } from '@hefe/utils';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import TableColumnSelectionForm from '../../forms/table-column-selection/table-column-selection.form';
import CustomTableColumnTransferComponent from './custom-table-column-transfer.component';
import styles from './custom-table.component.module.scss';
import { downloadCSV, getNodeText } from './custom-table.helpers';

export interface CustomTableProps {
  rowKey: any;
  name: string;
  dataSource: any[];
  columns: CustomTableColumn[];
  toolbarItems?: any[];
  displayToolbarItemsFirst?: boolean;
  loading?: boolean;
  hideColumnSelector?: boolean;
  rowSelection?: boolean;
  selectedRowKeys?: any[]; // ability to Override state row keys
  disabledRowKeys?: any[];
  strikeThroughRowKeys?: any[];
  warningRowKeys?: any[];
  initSelectedRowKeys?: any[];
  onRowSelect?: (selectedRowKeys: any[]) => void;
  onRowClick?: (item: any) => void;
  addForm?: OpenModalActionPayload;
  deleteAction?: {
    action: (data: any[], resetKeys?: () => void) => void;
    text?: string;
    multiDelete?: boolean;
    loading?: boolean;
  };
}

interface DateFilter {
  dateFrom: dayjs.Dayjs;
  dateTo: dayjs.Dayjs;
}

/**
 *
 *  Target Keys set when something changes in transfer component
 *
 *  Keys saved in store onOk of Modal and when user changes preset via Select
 *
 */
export default function CustomTable(props: CustomTableProps) {
  const { addForm, columns, dataSource, deleteAction, name, toolbarItems } =
    props;

  const dispatch = useAppDispatch();

  // Table Store
  const defaultColumnKeys = columns
    .filter((column) => column.default === true)
    .map((column) => column.key);
  const tableStore = useSelector(TablesSlice.tablesSelector)[name] || {
    currentTableKeys: defaultColumnKeys,
    presets: [],
  };

  const pageSize = useSelector(TablesSlice.tablesPageSizeSelector) || 10;

  // Transfer Modal
  const [targetKeys, setTargetKeys] = useState<string[]>(
    tableStore
      ? tableStore.currentTableKeys.length > 0
        ? tableStore.currentTableKeys
        : defaultColumnKeys
      : defaultColumnKeys
  );
  const [initialTargetKeys, setInitialTargetKeys] = useState<string[]>([]);
  const [isColumnSelectorOpen, setColumnSelectorOpen] = useState(false);

  const [dateFilters, setDateFilters] = useState<{ [key: string]: DateFilter }>(
    {}
  );

  // Presets
  const [activePreset, setActivePreset] = useState<string>();

  // Row selections
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>(
    props.initSelectedRowKeys ?? []
  );

  // Search
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    searchForActivePreset();
  }, [tableStore.presets]);

  useEffect(() => {
    const presets = tableStore?.presets;
    if (presets) {
      const foundPreset = presets.find((preset) => preset.id === activePreset);
      if (foundPreset) {
        const keys = foundPreset.keys;
        setTargetKeys(keys);
        saveTargetKeys(keys);
      }
    }
  }, [activePreset]);

  const searchForActivePreset = () => {
    const presets = tableStore?.presets;
    if (presets) {
      const foundPreset = presets.find(
        (preset) => preset.keys.join(',') === targetKeys.join(',')
      );

      if (foundPreset) {
        setActivePreset(foundPreset.id);
        return;
      }
    }

    setActivePreset(undefined);
  };

  const onModalOk = () => {
    saveTargetKeys(targetKeys);
    setColumnSelectorOpen(false);
    searchForActivePreset();
  };

  const openModal = () => {
    setInitialTargetKeys(targetKeys);
    setColumnSelectorOpen(true);
  };

  const closeModal = () => {
    setTargetKeys(initialTargetKeys);
    setColumnSelectorOpen(false);
  };

  const openSavePresettingModal = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: t('save_column_selection'),
        formId: `table-column-selection-form-${name}`,
        component: (
          <TableColumnSelectionForm tableName={name} keys={targetKeys} />
        ),
      })
    );
  };

  const openAddModal = () => {
    if (addForm) {
      dispatch(ModalsSlice.modalsActions.openModal(addForm));
    }
  };

  const resetSelectedRowKeys = () => {
    setSelectedRowKeys([]);
  };

  const deleteActivePreset = () => {
    if (activePreset) {
      dispatch(
        TablesSlice.tablesActions.deletePreset({
          tableName: name,
          id: activePreset,
        })
      );
    }
  };

  const saveTargetKeys = (keys: string[]) => {
    dispatch(
      TablesSlice.tablesActions.setTableKeys({
        tableName: name,
        keys: keys,
      })
    );
  };

  const getValueOfDataIndex = (column: any, item: any) => {
    // If data is array -> get nested attribute
    const dataIndex = column.dataIndex;

    if (Array.isArray(dataIndex)) {
      let indexFound = true;
      let object = item[dataIndex[0]];
      for (let i = 1; i < dataIndex.length; i++) {
        const nestedIndex = dataIndex[i];
        if (object) {
          object = object[nestedIndex];
        } else {
          indexFound = false;
        }
      }
      if (indexFound) {
        if (column.render) {
          return getNodeText(column.render(object, item));
        } else {
          return object;
        }
      }
    } else {
      if (column.render) {
        return getNodeText(
          column.render(dataIndex ? item[dataIndex] : item, item)
        );
      } else {
        const data = item[dataIndex];
        if (data) {
          return data;
        }
      }
    }
  };

  const getColDataIndex = (dataIndex: string | string[]): string => {
    if (Array.isArray(dataIndex)) {
      return dataIndex.join('.');
    } else {
      return dataIndex;
    }
  };

  /**
   *  Get active table columns and apply filter
   */
  const tableColumns = () => {
    if (targetKeys) {
      if (tableStore.currentTableKeys.length > 0) {
        const returnValue: any = [];
        for (const key of tableStore.currentTableKeys) {
          const col = columns.find((column) => column.key === key);
          if (col) {
            const colDataIndex = getColDataIndex(col.dataIndex || '');
            /********************************************************************************************************************
             *
             *                                            ADD FILTER TO COLUMNS
             *
             ******************************************************************************************************************/
            if (col.showFilter) {
              const filters = new Set<string>();
              props.dataSource?.forEach((item) => {
                const value = getValueOfDataIndex(col, item);
                if (value && value !== '') {
                  filters.add(value);
                }
              });

              // Add filters to column
              col.filters = Array.from(filters).map((filter) => {
                return {
                  text: filter as string,
                  value: filter as string,
                };
              });

              // Apply filter logic
              col.onFilter = (value: string, record: any) => {
                const recordValue = getValueOfDataIndex(col, record);
                if (recordValue) {
                  return recordValue.indexOf(value) === 0;
                } else {
                  return false;
                }
              };
            }
            // Apply special Date filter (between two dates)
            else if (col.showDateFilter) {
              col.onFilter = (value: dayjs.Dayjs[], record: any) => {
                const recordDate = getValueOfDataIndex(col, record);
                const dateFrom = value[0].startOf('day');
                const dateTo = value[1].endOf('day');

                if (recordDate) {
                  return dayjs(recordDate, dateFormat()).isBetween(
                    dateFrom,
                    dateTo,
                    undefined,
                    '[]'
                  );
                }
                return false;
              };
              col.filterDropdown = ({
                setSelectedKeys,
                confirm,
                clearFilters,
              }: any) => {
                const dates = dateFilters[colDataIndex];
                const dateFrom = dates
                  ? dates.dateFrom.isValid()
                    ? dates.dateFrom
                    : dayjs()
                  : dayjs();
                const dateTo = dates
                  ? dates.dateTo.isValid()
                    ? dates.dateTo
                    : dayjs()
                  : dayjs();

                return (
                  <Space key={key} direction="vertical" style={{ padding: 10 }}>
                    <DatePicker.RangePicker
                      value={[
                        dateFrom ? dayjs(dateFrom.toISOString()) : dayjs(),
                        dateTo ? dayjs(dateTo.toISOString()) : dayjs(),
                      ]}
                      format={dateFormat()}
                      allowClear
                      allowEmpty={[true, true]}
                      onChange={(dates: any, dateStrings: any) => {
                        const newDateFilters = { ...dateFilters };
                        const dateFiltersIndex = colDataIndex;
                        newDateFilters[dateFiltersIndex] = {
                          dateFrom: dayjs(dates[0]),
                          dateTo: dayjs(dates[1]),
                        };
                        setDateFilters(newDateFilters);
                      }}
                    />
                    <Divider style={{ margin: 2 }} />
                    <Space>
                      <Button
                        onClick={() => {
                          const newDateFilters = { ...dateFilters };
                          delete newDateFilters[colDataIndex];
                          setDateFilters(newDateFilters);
                          clearFilters();
                          confirm();
                        }}
                        size="small"
                      >
                        {t('reset')}
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedKeys([[dateFrom, dateTo]]);
                          confirm();
                        }}
                        size="small"
                      >
                        OK
                      </Button>
                    </Space>
                  </Space>
                );
              };
            }

            /********************************************************************************************************************
             *
             *                                            ADD SORTER TO COLUMNS
             *
             ******************************************************************************************************************/
            if (col.sortType) {
              col.sorter = (a: any, b: any) => {
                const valueA = getValueOfDataIndex(col, a) || '';
                const valueB = getValueOfDataIndex(col, b) || '';

                switch (col.sortType) {
                  case 'string':
                    return valueA?.localeCompare(valueB);
                  case 'number': {
                    const numberA = parseFloat(valueA) || 0;
                    const numberB = parseFloat(valueB) || 0;
                    return numberA - numberB;
                  }
                  case 'date': {
                    let dateA = dayjs(valueA, dateFormat());
                    let dateB = dayjs(valueB, dateFormat());

                    // set date to 1970 if invalid
                    if (!dateA.isValid()) {
                      dateA = dayjs('1970-01-01');
                    }
                    if (!dateB.isValid()) {
                      dateB = dayjs('1970-01-01');
                    }

                    return dateA.diff(dateB);
                  }
                  default:
                    return valueA - valueB;
                }
              };
            }

            returnValue.push(col);
          }
        }
        return returnValue;
      }
    }

    return columns;
  };

  const getDataSource = () => {
    if (searchText.length < 3) {
      return dataSource;
    }

    const returnValue: any[] = [];

    for (const item of dataSource) {
      let containsSearchText = true; // Check if item matches search text
      const searchParts = searchText.toLowerCase().trim().split(' ');
      for (const searchPart of searchParts) {
        let foundSearchPart = false;
        for (const key of tableStore.currentTableKeys) {
          const col = columns.find((column) => column.key === key);
          if (col) {
            const colDataIndex = getColDataIndex(col.dataIndex || '');
            if (colDataIndex || col.render) {
              const value = getNodeText(getValueOfDataIndex(col, item)) + '';
              if (value) {
                if (value.toLowerCase().includes(searchPart)) {
                  foundSearchPart = true;
                }
              }
            }
          }
        }
        if (!foundSearchPart) {
          containsSearchText = false;
          break;
        }
      }

      if (containsSearchText) {
        returnValue.push(item);
      }
    }

    return _.uniqBy(returnValue, props.rowKey);
  };

  const renderPresetSelect = () => {
    const presets = tableStore?.presets;
    if (presets && presets.length > 0) {
      return (
        <Select
          key="preset-select"
          style={{ width: '90%' }}
          value={activePreset}
          placeholder={t('own_column_arrangements')}
          onChange={(uuid: string) => setActivePreset(uuid)}
        >
          {presets.map((preset) => {
            return (
              <Select.Option key={preset.id} value={preset.id}>
                {preset.name}
              </Select.Option>
            );
          })}
        </Select>
      );
    }

    return null;
  };

  return (
    <Row gutter={[0, 20]}>
      <Col xs={24}>
        <Row justify="space-between" gutter={[0, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Row>
              <Col xs={24}>
                <Space>
                  {props.displayToolbarItemsFirst && toolbarItems}
                  {addForm != null && (
                    <Tooltip
                      title={addForm.disabledTooltip}
                      open={addForm.disabledTooltip ? undefined : false}
                    >
                      <Button
                        disabled={!!addForm.disabledTooltip}
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openAddModal()}
                      />
                    </Tooltip>
                  )}
                  {deleteAction != null && (
                    <Popconfirm
                      title={deleteAction.text || `${t('questions.delete')}`}
                      disabled={
                        deleteAction.loading ||
                        selectedRowKeys.length === 0 ||
                        (!deleteAction.multiDelete &&
                          selectedRowKeys.length > 1)
                      }
                      onConfirm={() =>
                        deleteAction
                          ? deleteAction.action(
                              selectedRowKeys,
                              resetSelectedRowKeys
                            )
                          : null
                      }
                    >
                      <Button
                        loading={deleteAction.loading}
                        disabled={
                          selectedRowKeys.length === 0 ||
                          (!deleteAction.multiDelete &&
                            selectedRowKeys.length > 1)
                        }
                        type="default"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  )}
                  {!props.displayToolbarItemsFirst && toolbarItems}
                </Space>
              </Col>
            </Row>
          </Col>
          {!props.hideColumnSelector && (
            <Col xs={24} sm={12} md={12}>
              <Row justify="end">
                <Col xs={16}>{renderPresetSelect()}</Col>
                <Col xs={8}>
                  <Space>
                    <Button.Group>
                      <Button
                        disabled={activePreset !== undefined}
                        icon={<SaveOutlined />}
                        onClick={() => openSavePresettingModal()}
                      />
                      <Popconfirm
                        disabled={activePreset === undefined}
                        title={t('delete') + '?'}
                        onConfirm={() => deleteActivePreset()}
                      >
                        <Button
                          disabled={activePreset === undefined}
                          icon={<DeleteOutlined />}
                          onClick={() => null}
                        />
                      </Popconfirm>
                    </Button.Group>
                    <Tooltip title={t('select_columns')}>
                      <Button
                        icon={<TableOutlined />}
                        onClick={() => openModal()}
                      />
                    </Tooltip>
                    <Tooltip title={t('download_csv')}>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => downloadCSV(props.name)}
                      />
                    </Tooltip>
                  </Space>
                </Col>
                <Modal
                  open={isColumnSelectorOpen}
                  width={800}
                  onOk={() => onModalOk()}
                  onCancel={() => closeModal()}
                >
                  <CustomTableColumnTransferComponent
                    {...props}
                    setTargetKeys={setTargetKeys}
                    targetKeys={targetKeys}
                  />
                </Modal>
              </Row>
            </Col>
          )}
        </Row>
      </Col>
      <Col xs={24}>
        <Input
          placeholder={`${t('search')}...`}
          onChange={(event) => setSearchText(event.target.value)}
          allowClear
          autoComplete="false"
          autoCorrect="false"
        />
      </Col>
      <Col xs={24}>
        <Table
          id={props.name}
          rowKey={props.rowKey}
          dataSource={getDataSource()}
          columns={tableColumns()}
          loading={props.loading}
          pagination={{
            pageSize: pageSize,
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100, 250],
            onChange: (page, newPageSize) => {
              dispatch(TablesSlice.tablesActions.setPageSize(newPageSize));
            },
            showTotal: (total, range) =>
              t('table.pagination.total', {
                from: range[0],
                to: range[1],
                total: total,
              }),
          }}
          rowClassName={(item: any) => {
            const classes: string[] = [];
            classes.push(
              styles[`row-click-${props.onRowClick ? 'enabled' : 'disabled'}`]
            );
            if (props.strikeThroughRowKeys) {
              if (props.strikeThroughRowKeys.includes(item[props.rowKey])) {
                classes.push(styles['strike-through']);
              }
            } else if (props.warningRowKeys) {
              if (props.warningRowKeys.includes(item[props.rowKey])) {
                classes.push(styles['warning']);
              }
            }
            return classes.join(' ');
          }}
          rowSelection={
            props.rowSelection || props.deleteAction
              ? {
                  selectedRowKeys: props.selectedRowKeys ?? selectedRowKeys,
                  onChange: (keys) => {
                    setSelectedRowKeys(keys);
                    if (props.onRowSelect) {
                      props.onRowSelect(keys);
                    }
                  },
                  getCheckboxProps: (key) => {
                    if (props.disabledRowKeys) {
                      const rowKey = key[props.rowKey];
                      if (rowKey) {
                        return {
                          disabled: props.disabledRowKeys.includes(rowKey),
                        };
                      }
                    }
                    return {
                      disabled: false,
                    };
                  },
                }
              : undefined
          }
          onRow={(record, rowIndex) => {
            return {
              onClick: (event: BaseSyntheticEvent) => {
                if (props.onRowClick) {
                  // Don't fire onRowClick if user just misses checkbox
                  if (props.rowSelection || props.deleteAction) {
                    const columnIndex = event?.target?.cellIndex;
                    if (columnIndex === 0) {
                      return;
                    }
                  }
                  props.onRowClick(record);
                }
              }, // click row
            };
          }}
          scroll={{
            x: true,
          }}
        />
      </Col>
    </Row>
  );
}
